<template>
  <div>
    <div
      v-if="!loading"
      class="report-table"
      :data-testid="getTestId('site-overview-section')"
    >
      <h3 :data-testid="getTestId('site-overview-title')">
        {{ $t('reportSummary.siteOverview') }}
      </h3>
      <table :data-testid="getTestId('site-overview-table')">
        <thead>
          <th>{{ $t('reportSummary.siteName') }}</th>
          <th>{{ $t('reportSummary.address') }}</th>
          <th>{{ $t('reportSummary.powerCapacityKw') }}</th>
        </thead>
        <tbody>
          <tr>
            <td :data-testid="getTestId('site-name')">
              {{ reportSummary ? reportSummary.siteName : '-' }}
            </td>
            <td :data-testid="getTestId('site-address')">
              {{ reportSummary ? reportSummary.address : '-' }}
            </td>
            <td :data-testid="getTestId('power-capacity')">
              {{ reportSummary ? Number(reportSummary.powerCapacity || 0).toFixed(1) : '0' }}
            </td>
          </tr>
        </tbody>

        <thead>
          <th>{{ $t('reportSummary.pvModuleCount') }}</th>
          <th>{{ $t('reportSummary.pvModuleModelMaker') }}</th>
          <th>{{ $t('reportSummary.establishedDate') }}</th>
        </thead>
        <tbody>
          <tr>
            <td :data-testid="getTestId('pv-module-count')">
              {{ reportSummary ? reportSummary.pvModuleCount : '0' }}
            </td>
            <td :data-testid="getTestId('pv-module-maker')">
              {{ getPvModuleMakerInfo() }}
            </td>
            <td :data-testid="getTestId('established-date')">
              {{ formatReportDisplayDate(reportSummary?.establishedDate) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else class="loading-section" :data-testid="getTestId('loading-section')">
      <v-skeleton-loader type="heading" width="35%" class="mb-3" />
      <table class="report-table">
        <thead>
          <th>{{ $t('reportSummary.siteName') }}</th>
          <th>{{ $t('reportSummary.address') }}</th>
          <th>{{ $t('reportSummary.powerCapacityKw') }}</th>
        </thead>
        <tbody>
          <tr>
            <td v-for="n in 3" :key="'site-sk-' + n">
              <v-skeleton-loader type="text" width="85%" />
            </td>
          </tr>
        </tbody>
        <thead>
          <th>{{ $t('reportSummary.pvModuleCount') }}</th>
          <th>{{ $t('reportSummary.pvModuleModelMaker') }}</th>
          <th>{{ $t('reportSummary.establishedDate') }}</th>
        </thead>
        <tbody>
          <tr>
            <td v-for="n in 3" :key="'pv-sk-' + n">
              <v-skeleton-loader type="text" width="70%" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div
      v-if="!loading"
      class="report-table"
      :data-testid="getTestId('inspection-overview-section')"
    >
      <h3 :data-testid="getTestId('inspection-overview-title')">
        {{ $t('reportSummary.inspectionOverview') }}
      </h3>
      <table :data-testid="getTestId('inspection-overview-table')">
        <thead>
          <th>{{ $t('reportSummary.inspectionCompany') }}</th>
          <th>{{ $t('reportSummary.inspectorName') }}</th>
          <th>{{ $t('reportSummary.phone') }}</th>
          <th>{{ $t('reportSummary.email') }}</th>
        </thead>
        <tbody>
          <tr>
            <td :data-testid="getTestId('inspection-company')">
              {{ getValueOrDefault(reportSummary?.inspectionCompanyName) }}
            </td>
            <td :data-testid="getTestId('inspector-name')">
              {{ getValueOrDefault(reportSummary?.inspectorName) }}
            </td>
            <td :data-testid="getTestId('inspector-phone')">
              {{ getValueOrDefault(reportSummary?.inspectorPhoneNo) }}
            </td>
            <td :data-testid="getTestId('inspector-email')">
              {{ getValueOrDefault(reportSummary?.inspectorEmailAddr) }}
            </td>
          </tr>
        </tbody>

        <thead>
          <th>{{ $t('reportSummary.examineDateTime') }}</th>
          <th>{{ $t('reportSummary.irradianceWm2') }}</th>
          <th>{{ $t('reportSummary.usedDrone') }}</th>
          <th>{{ $t('reportSummary.usedCamera') }}</th>
        </thead>
        <tbody>
          <tr>
            <td :data-testid="getTestId('examine-date')">
              {{ formatReportDisplayDate(reportSummary?.examineDate) }}
            </td>
            <td :data-testid="getTestId('irradiance')">
              {{ getValueOrDefault(reportSummary?.irradiance) }}
            </td>
            <td :data-testid="getTestId('inspection-drone')">
              {{ getValueOrDefault(reportSummary?.inspectionDrone) }}
            </td>
            <td :data-testid="getTestId('inspection-camera')">
              {{ getValueOrDefault(reportSummary?.inspectionCamera) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="!loading"
      class="report-table"
      :data-testid="getTestId('project-companies-section')"
    >
      <h3 :data-testid="getTestId('project-companies-title')">
        {{ $t('reportSummary.relatedCompanies') }}
      </h3>
      <table :data-testid="getTestId('project-companies-table')">
        <thead>
          <th>{{ $t('reportSummary.item') }}</th>
          <th>{{ $t('reportSummary.info') }}</th>
        </thead>
        <tbody>
          <tr>
            <td>{{ $t('reportSummary.operationCompany') }}</td>
            <td :data-testid="getTestId('operation-company')">
              {{ getSite?.operationCompany?.companyName || '-' }}
            </td>
          </tr>
          <tr>
            <td>{{ $t('reportSummary.ownerCompany') }}</td>
            <td :data-testid="getTestId('owner-company')">
              {{ getSite?.ownerCompany?.companyName || '-' }}
            </td>
          </tr>
          <tr>
            <td>{{ $t('reportSummary.projectName') }}</td>
            <td :data-testid="getTestId('project-name')">
              {{ getSite?.project?.name || getSite?.name || '-' }}
            </td>
          </tr>
          <tr>
            <td>{{ $t('reportSummary.managerName') }}</td>
            <td>
              {{ reportSummary?.companyManager ? reportSummary?.companyManager?.name : '-' }}
            </td>
          </tr>
          <tr>
            <td>{{ $t('reportSummary.managerPhone') }}</td>
            <td>
              {{ reportSummary?.companyManager ? reportSummary?.companyManager?.phone : '-' }}
            </td>
          </tr>
          <tr>
            <td>{{ $t('reportSummary.managerEmail') }}</td>
            <td>
              {{ reportSummary?.companyManager ? reportSummary?.companyManager?.email : '-' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="!loading"
      class="report-table page-break-before"
      :data-testid="getTestId('findings-section')"
    >
      <h3 :data-testid="getTestId('findings-title')">
        {{ $t('reportSummary.findings') }}
      </h3>
      <table :data-testid="getTestId('findings-table')">
        <thead class="wide-thead">
          <th>{{ $t('reportSummary.defectType') }}</th>
          <th>{{ $t('reportSummary.defectCount') }}</th>
          <th>{{ $t('reportSummary.defectModuleCount') }}</th>
          <th>{{ $t('reportSummary.moduleDefectRate') }}</th>
          <th>{{ $t('reportSummary.estimatedPowerLossKw') }}</th>
          <th>{{ $t('reportSummary.estimatedPowerLossPercent') }}</th>
        </thead>

        <tbody>
          <template v-if="!!reportSummary">
            <tr
              v-for="defectLoss in reportSummary.defectReportSummary?.defectPerPowerLosses"
              :key="defectLoss.defectType.id"
              :class="`defect-severity-${getDefectSeverity(defectLoss.estimatedPowerLossPercent)}`"
              :data-testid="getTestId(`defect-row-${defectLoss.defectType.id}`)"
            >
              <td :data-testid="getTestId(`defect-name-${defectLoss.defectType.id}`)">
                {{ localizedDefectTypeName(defectLoss?.defectType?.code, defectLoss?.defectName) }}
              </td>
              <td :data-testid="getTestId(`defect-count-${defectLoss.defectType.id}`)">
                {{ defectLoss?.defectCount }}
              </td>
              <td :data-testid="getTestId(`defect-module-count-${defectLoss.defectType.id}`)">
                {{ defectLoss?.defectModuleCount }}
              </td>
              <td :data-testid="getTestId(`defect-percent-${defectLoss.defectType.id}`)">
                {{
                  Number.isFinite(defectLoss?.defectPercent)
                    ? defectLoss?.defectPercent.toFixed(2) + '%'
                    : '-'
                }}
              </td>
              <td :data-testid="getTestId(`power-loss-value-${defectLoss.defectType.id}`)">
                {{ defectLoss?.estimatedPowerLossValue.toFixed(2) }}
              </td>
              <td
                :data-testid="getTestId(`power-loss-percent-${defectLoss.defectType.id}`)"
                class="power-loss-cell"
              >
                <span
                  :class="`severity-${getDefectSeverity(defectLoss?.estimatedPowerLossPercent)}`"
                >
                  {{
                    Number.isFinite(defectLoss?.estimatedPowerLossPercent)
                      ? defectLoss?.estimatedPowerLossPercent.toFixed(2) + '%'
                      : '-'
                  }}
                </span>
              </td>
            </tr>
          </template>

          <tr class="tr-result" :data-testid="getTestId('total-row')">
            <td>{{ $t('reportSummary.totalRowLabel') }}</td>
            <td :data-testid="getTestId('total-defect-count')">
              {{ reportSummary ? reportSummary?.defectReportSummary?.totalDefectCount : '0' }}
            </td>
            <td :data-testid="getTestId('total-defect-module-count')">
              {{ reportSummary ? reportSummary?.defectReportSummary?.totalDefectModuleCount : '0' }}
            </td>
            <td :data-testid="getTestId('total-defect-module-percent')">
              {{
                reportSummary
                  ? Number.isFinite(reportSummary?.defectReportSummary?.totalDefectModulePercent)
                    ? Math.round(reportSummary?.defectReportSummary?.totalDefectModulePercent) + '%'
                    : '-'
                  : '-'
              }}
            </td>
            <td :data-testid="getTestId('total-estimated-power-loss-value')">
              {{
                reportSummary
                  ? reportSummary?.defectReportSummary?.totalEstimatedPowerLossValue.toFixed(2)
                  : '0'
              }}
            </td>
            <td :data-testid="getTestId('total-estimated-power-loss-percent')">
              {{
                reportSummary
                  ? Number.isFinite(
                      reportSummary?.defectReportSummary?.totalEstimatedPowerLossPercent
                    )
                    ? reportSummary?.defectReportSummary?.totalEstimatedPowerLossPercent.toFixed(2) +
                      '%'
                    : '-'
                  : '-'
              }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!loading" class="legal-notice">
      <h3>{{ $t('reportSummary.legalNoticeTitle') }}</h3>
      <ul>
        <li>{{ $t('reportSummary.legalNotice1') }}</li>
        <li>{{ $t('reportSummary.legalNotice2') }}</li>
        <li>{{ $t('reportSummary.legalNotice3') }}</li>
        <li>{{ $t('reportSummary.legalNotice4') }}</li>
        <li>{{ $t('reportSummary.legalNotice5') }}</li>
      </ul>
    </div>

    <div v-if="!loading" class="appendix-section page-break-before">
      <h2 class="appendix-title">
        {{ $t('reportSummary.appendixTitle') }}
      </h2>

      <div class="report-table">
        <h3>{{ $t('reportSummary.pvSpecTitle') }}</h3>
        <table>
          <thead>
            <th>{{ $t('reportSummary.item') }}</th>
            <th>{{ $t('reportSummary.info') }}</th>
            <th>{{ $t('reportSummary.unit') }}</th>
            <th>{{ $t('reportSummary.note') }}</th>
          </thead>
          <tbody>
            <tr>
              <td>{{ $t('reportSummary.productName') }}</td>
              <td>{{ reportSummary?.panelProduct?.modelName || '-' }}</td>
              <td>-</td>
              <td>{{ $t('reportSummary.panelModelName') }}</td>
            </tr>
            <tr>
              <td>{{ $t('reportSummary.maker') }}</td>
              <td>{{ reportSummary?.panelProduct?.maker || '-' }}</td>
              <td>-</td>
              <td>{{ $t('reportSummary.makerCompany') }}</td>
            </tr>
            <tr>
              <td>{{ $t('reportSummary.moduleWidth') }}</td>
              <td>{{ reportSummary?.panelProduct?.width || '-' }}</td>
              <td>mm</td>
              <td>{{ $t('reportSummary.moduleWidthDesc') }}</td>
            </tr>
            <tr>
              <td>{{ $t('reportSummary.moduleHeight') }}</td>
              <td>{{ reportSummary?.panelProduct?.height || '-' }}</td>
              <td>mm</td>
              <td>{{ $t('reportSummary.moduleHeightDesc') }}</td>
            </tr>
            <tr>
              <td>{{ $t('reportSummary.ratedOutputPerModule') }}</td>
              <td>{{ getPvModuleOutputFromPanel() }}</td>
              <td>W</td>
              <td>{{ $t('reportSummary.ratedOutputNote') }}</td>
            </tr>
            <tr>
              <td>{{ $t('reportSummary.cellCount') }}</td>
              <td>{{ reportSummary?.panelProduct?.numberOfCells || '-' }}</td>
              <td>{{ $t('reportSummary.countUnit') }}</td>
              <td>{{ $t('reportSummary.cellCountDesc') }}</td>
            </tr>
            <tr>
              <td>{{ $t('reportSummary.cellType') }}</td>
              <td>{{ getPanelCellType() }}</td>
              <td>-</td>
              <td>{{ $t('reportSummary.solarCellTypeDesc') }}</td>
            </tr>
            <tr>
              <td>{{ $t('reportSummary.operatingTempRange') }}</td>
              <td style="letter-spacing: 0.1em">
                {{ getOperatingTempRange() }}
              </td>
              <td>°C</td>
              <td>{{ $t('reportSummary.operatingTempRangeDesc') }}</td>
            </tr>
            <tr>
              <td>{{ $t('reportSummary.warrantyPeriod') }}</td>
              <td>{{ reportSummary?.panelProduct?.warrantyPeriodYears || '-' }}</td>
              <td>{{ $t('reportSummary.yearUnit') }}</td>
              <td>{{ $t('reportSummary.warrantyDesc') }}</td>
            </tr>
            <tr>
              <td>{{ $t('reportSummary.cellEfficiency') }}</td>
              <td>{{ reportSummary?.panelProduct?.cellEfficiency || '-' }}</td>
              <td>%</td>
              <td>{{ $t('reportSummary.cellEfficiencyDesc') }}</td>
            </tr>
            <tr>
              <td>{{ $t('reportSummary.degradationRate') }}</td>
              <td>{{ reportSummary?.panelProduct?.degradationRate || '-' }}</td>
              <td>%</td>
              <td>{{ $t('reportSummary.degradationRateDesc') }}</td>
            </tr>
            <tr>
              <td>{{ $t('reportSummary.ipRating') }}</td>
              <td>{{ reportSummary?.panelProduct?.ipRating || '-' }}</td>
              <td>-</td>
              <td>{{ $t('reportSummary.ipRatingDesc') }}</td>
            </tr>
            <tr>
              <td>{{ $t('reportSummary.maxCurrent') }}</td>
              <td>{{ reportSummary?.panelProduct?.maxCurrent || '-' }}</td>
              <td>A</td>
              <td>{{ $t('reportSummary.maxCurrentDesc') }}</td>
            </tr>
            <tr>
              <td>{{ $t('reportSummary.maxVoltage') }}</td>
              <td>{{ reportSummary?.panelProduct?.maxVoltage || '-' }}</td>
              <td>V</td>
              <td>{{ $t('reportSummary.maxVoltageDesc') }}</td>
            </tr>
            <tr>
              <td>{{ $t('reportSummary.openCircuitVoltage') }}</td>
              <td>{{ reportSummary?.panelProduct?.openCircuitVoltage || '-' }}</td>
              <td>V</td>
              <td>{{ $t('reportSummary.openCircuitVoltageDesc') }}</td>
            </tr>
            <tr>
              <td>{{ $t('reportSummary.shortCircuitCurrent') }}</td>
              <td>{{ reportSummary?.panelProduct?.shortCircuitCurrent || '-' }}</td>
              <td>A</td>
              <td>{{ $t('reportSummary.shortCircuitCurrentDesc') }}</td>
            </tr>
            <tr>
              <td>{{ $t('reportSummary.moduleThickness') }}</td>
              <td>{{ reportSummary?.panelProduct?.thickness || '-' }}</td>
              <td>mm</td>
              <td>{{ $t('reportSummary.moduleThicknessDesc') }}</td>
            </tr>
            <tr>
              <td>{{ $t('reportSummary.outputTolerance') }}</td>
              <td>{{ reportSummary?.panelProduct?.tolerance || '-' }}</td>
              <td>%</td>
              <td>{{ $t('reportSummary.outputToleranceDesc') }}</td>
            </tr>
            <tr>
              <td>{{ $t('reportSummary.moduleWeight') }}</td>
              <td>{{ reportSummary?.panelProduct?.weight || '-' }}</td>
              <td>kg</td>
              <td>{{ $t('reportSummary.moduleWeightDesc') }}</td>
            </tr>
            <tr>
              <td>{{ $t('reportSummary.moduleEfficiency') }}</td>
              <td>{{ calculateModuleEfficiency() }}</td>
              <td>%</td>
              <td>{{ $t('reportSummary.moduleEfficiencyDesc') }}</td>
            </tr>
            <tr>
              <td>{{ $t('reportSummary.totalCapacity') }}</td>
              <td>{{ formatPowerCapacity() }}</td>
              <td>kW</td>
              <td>{{ $t('reportSummary.totalCapacityNote') }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!loading && defectTypes.length > 0" class="report-table">
        <h3>{{ $t('reportSummary.defectTypeTableTitle') }}</h3>
        <table>
          <thead>
            <th>{{ $t('reportSummary.defectTypeName') }}</th>
            <th>{{ $t('reportSummary.defectDescription') }}</th>
          </thead>
          <tbody>
            <tr
              v-for="defectType in defectTypes"
              :key="defectType.id"
              :data-testid="getTestId(`defect-type-row-${defectType.id}`)"
            >
              <td :data-testid="getTestId(`defect-type-name-${defectType.id}`)">
                {{ localizedDefectTypeName(defectType.code, defectType.name) }}
              </td>
              <td
                :data-testid="getTestId(`defect-type-description-${defectType.id}`)"
                style="text-align: left"
              >
                {{ getDefectTypeDescription(defectType) }}
              </td>
            </tr>
          </tbody>
        </table>

        <div class="reference-note">
          <p>
            <strong>{{ $t('reportSummary.reference') }}:</strong>
            {{ $t('reportSummary.referenceNote') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { getTestId } from '@/utils/testUtils';
  import { mapGetters } from 'vuex';
  import { getCellTypeLabel } from '@/utils/panelProductUtils';
  import {
    formatReportDate,
    localizeDefectTypeDescription,
    localizeDefectTypeLabel,
    normalizePanelProductCellTypeCode
  } from '@/utils/reportLocaleUtils';

  /**
   * 검사 보고서(요약/상세)의 공통 본문 섹션.
   * 발전소 개요 · 검사 개요 · 관련 회사 · 검사 결과 · 법적 고지 · 부록(PV 사양/결함 유형)을 렌더링한다.
   * 요약 보고서와 상세 보고서가 1~4장을 공유하기 위해 분리한 컴포넌트이다.
   */
  export default {
    name: 'ReportCommonSections',
    props: {
      reportSummary: {
        type: Object,
        default: null
      },
      loading: {
        type: Boolean,
        default: false
      }
    },
    data: () => ({
      defectTypes: []
    }),
    computed: {
      ...mapGetters({
        getSite: 'explorer/getSite'
      }),
      uiLocale() {
        return this.$i18n.locale;
      }
    },
    watch: {
      reportSummary: {
        handler(val) {
          if (val) {
            this.loadDefectTypes();
          }
        },
        immediate: true
      }
    },
    methods: {
      getTestId(id) {
        return getTestId(id);
      },

      formatReportDisplayDate(value) {
        void this.uiLocale;
        return formatReportDate(value, this.$i18n.locale);
      },

      localizedDefectTypeName(code, fallback) {
        void this.uiLocale;
        return localizeDefectTypeLabel(
          key => this.$te(key),
          key => this.$t(key),
          code,
          fallback
        );
      },

      /**
       * 결함 유형 설명 — locale(ko/en)의 defectTypes.{CODE} 우선, 없으면 API description
       */
      getDefectTypeDescription(defectType) {
        void this.uiLocale;
        return localizeDefectTypeDescription(
          key => this.$te(key),
          key => this.$t(key),
          defectType?.code,
          defectType?.description
        );
      },

      async loadDefectTypes() {
        try {
          const { defectTypeApi } = await import('../../apis/defect.type.api');
          const data = await defectTypeApi.getParentDefectTypes();
          this.defectTypes = data._embedded?.defectTypes || [];
        } catch (error) {
          console.error('[REPORT SECTIONS] 결함 유형 데이터 로드 실패:', error);
          this.defectTypes = [];
        }
      },

      getValueOrDefault(value) {
        if (value === null || value === undefined || value === '') {
          return '-';
        }
        if (typeof value === 'string' && value.trim() === '') {
          return '-';
        }
        return value;
      },

      getPvModuleMakerInfo() {
        if (this.reportSummary?.pvModuleMaker && this.reportSummary.pvModuleMaker.trim() !== '') {
          return this.reportSummary.pvModuleMaker;
        } else if (this.reportSummary?.panelProduct) {
          const modelName = this.reportSummary.panelProduct.modelName || '';
          const maker = this.reportSummary.panelProduct.maker || '';
          return `${modelName ? modelName : '-'} / ${maker ? maker : '-'}`;
        }
        return '-';
      },

      getPvModuleOutput() {
        if (this.reportSummary?.pvModuleOutput && this.reportSummary.pvModuleOutput > 0) {
          return this.reportSummary.pvModuleOutput;
        }
        if (this.reportSummary?.panelProduct?.wattage && this.reportSummary.panelProduct.wattage > 0) {
          return this.reportSummary.panelProduct.wattage;
        }
        return '0';
      },

      getPvModuleOutputFromPanel() {
        if (
          this.reportSummary?.panelProduct?.maxPowerOutput &&
          this.reportSummary.panelProduct.maxPowerOutput > 0
        ) {
          return this.reportSummary.panelProduct.maxPowerOutput;
        }
        if (this.reportSummary?.pvModuleOutput && this.reportSummary.pvModuleOutput > 0) {
          return this.reportSummary.pvModuleOutput;
        }
        return '-';
      },

      getPanelCellType() {
        void this.uiLocale;
        const raw =
          this.reportSummary?.panelProduct?.panelProductCellType ??
          this.reportSummary?.panelProduct?.cellType;
        const code = normalizePanelProductCellTypeCode(raw);
        if (code != null) {
          return getCellTypeLabel(code);
        }
        if (raw === null || raw === undefined || raw === '') {
          return '-';
        }
        return String(raw);
      },

      getOperatingTempRange() {
        const minTemp = this.reportSummary?.panelProduct?.operatingTempMin;
        const maxTemp = this.reportSummary?.panelProduct?.operatingTempMax;
        if (minTemp !== undefined && maxTemp !== undefined) {
          return `${minTemp} ~ ${maxTemp}`;
        } else if (minTemp !== undefined) {
          return `${minTemp} ~ -`;
        } else if (maxTemp !== undefined) {
          return `- ~ ${maxTemp}`;
        }
        return '-';
      },

      calculateModuleEfficiency() {
        const moduleOutput = this.getPvModuleOutput();
        if (!moduleOutput || moduleOutput === 0 || moduleOutput === '0') return '0';
        if (
          this.reportSummary?.panelProduct?.efficiency &&
          this.reportSummary.panelProduct.efficiency > 0
        ) {
          return this.reportSummary.panelProduct.efficiency.toFixed(1);
        }
        const numericOutput =
          typeof moduleOutput === 'string' ? parseFloat(moduleOutput) : moduleOutput;
        return ((numericOutput / 2000) * 100).toFixed(1);
      },

      formatPowerCapacity() {
        const powerCapacity = this.reportSummary?.powerCapacity || this.getSite?.powerCapacity;
        if (!powerCapacity) return '0';
        return Number(powerCapacity).toFixed(1);
      },

      getDefectSeverity(powerLossPercent) {
        if (!Number.isFinite(powerLossPercent)) return 'normal';
        if (powerLossPercent >= 2.0) return 'critical';
        if (powerLossPercent >= 0.5) return 'warning';
        return 'normal';
      }
    }
  };
</script>

<style scoped>
  .report-table {
    margin-top: 30px;
    margin-bottom: 25px;
  }

  .report-table table {
    margin: 20px;
    width: 96%;
    color: var(--v-text-base);
    border: 2px solid var(--v-text-base);
    border-collapse: collapse;
    padding-left: 30px;
    border-radius: 0px;
    overflow: hidden;
    box-shadow: 0 1px 3px color-mix(in srgb, var(--v-text-base) 20%, transparent);
    font-family: 'Arial', sans-serif;
  }

  .report-table table th,
  .report-table table td {
    border: 1px solid var(--v-text-base);
    padding: 8px 10px;
    vertical-align: middle;
  }

  .report-table table th {
    background: var(--v-surface-base);
    color: var(--v-text-base);
    font-weight: bold;
    border: 1px solid var(--v-text-base);
    text-align: center;
    font-size: 13px;
    letter-spacing: 0.3px;
  }

  .report-table table td {
    text-align: center;
    background: var(--v-surface-base);
  }

  .tr-result {
    font-weight: 800;
  }

  .power-loss-cell {
    text-align: center;
    font-weight: bold;
  }

  .defect-severity-critical,
  .defect-severity-warning,
  .defect-severity-normal {
    background-color: var(--v-surface-base) !important;
  }

  .severity-critical {
    color: var(--v-text-base) !important;
    font-weight: bold;
    text-decoration: underline;
  }

  .severity-warning {
    color: var(--v-text-base) !important;
    font-weight: bold;
  }

  .severity-normal {
    color: var(--v-text-base) !important;
    font-weight: normal;
  }

  .loading-section {
    min-height: 200px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .page-break-before {
    page-break-before: always;
    margin: 50px 0 0 0;
  }

  .report-table h3 {
    background-color: var(--v-surface-base);
    color: var(--v-text-base);
    padding: 12px;
    border-left: 4px solid var(--v-secondary-base);
    margin-bottom: 20px;
    border-radius: 4px;
    font-weight: bold;
    font-size: 16px;
    text-align: left;
    letter-spacing: 0.5px;
  }

  .appendix-section {
    margin-top: 40px;
    padding-top: 30px;
  }

  .appendix-title {
    font-size: 20px;
    font-weight: bold;
    color: var(--v-text-base);
    text-align: center;
    margin-bottom: 30px;
    padding: 20px;
    background-color: var(--v-surface-base);
    border-left: 6px solid var(--v-secondary-base);
    border-radius: 4px;
    border: 1px solid var(--v-secondary-base);
  }

  .legal-notice {
    margin-top: 20px;
    padding: 20px;
    background-color: var(--v-surface-base);
    border: 1px solid var(--v-secondary-base);
    border-radius: 5px;
  }

  .legal-notice h3 {
    background-color: var(--v-surface-base);
    color: var(--v-text-base);
    padding: 12px;
    border-left: 4px solid var(--v-secondary-base);
    margin-bottom: 20px;
    border-radius: 4px;
    font-weight: bold;
    font-size: 16px;
    text-align: left;
    letter-spacing: 0.5px;
  }

  .legal-notice ul {
    margin: 0;
    padding-left: 20px;
    color: var(--v-secondary-base);
    line-height: 1.6;
  }

  .legal-notice li {
    margin-bottom: 8px;
    font-size: 13px;
  }

  .reference-note {
    margin-top: 20px;
    padding: 15px;
    background-color: var(--v-surface-base);
    border: 1px solid var(--v-secondary-base);
    border-radius: 5px;
    font-size: 12px;
    color: var(--v-text-base);
    line-height: 1.5;
  }

  .reference-note p {
    margin: 0;
    padding: 0;
  }

  .reference-note strong {
    color: var(--v-text-base);
    font-weight: bold;
  }
</style>
